import type { Identifier } from "ra-core";
import type { Thread, ThreadNote } from "@/modules/threads/threadModel";

/** One "seen" record: a member, first and last time they saw a thread. */
export type ThreadView = {
  note_resource: ThreadNote["resource"];
  note_id: number;
  member_id: number;
  first_viewed_at: string;
  last_viewed_at: string;
  view_count: number;
};

export type ThreadHistoryEntry = {
  at: string;
  memberId?: Identifier | null;
  action: string;
  detail?: string | null;
};

/** Full date and time, e.g. "Oct 2, 2026, 3:30 PM". */
export const formatAuditDate = (value?: string | null) => {
  if (!value) return "—";
  const date = new Date(value);
  return Number.isNaN(date.getTime())
    ? value
    : date.toLocaleString("en-US", {
        month: "short",
        day: "numeric",
        year: "numeric",
        hour: "numeric",
        minute: "2-digit",
      });
};

/**
 * Everything that happened to a thread, oldest first: created, edited,
 * each reply (and its edit), pinned, resolved and who saw it.
 */
export const buildThreadHistory = (
  thread: Thread,
  views: ThreadView[],
  preview: (text?: string | null) => string,
): ThreadHistoryEntry[] => {
  const { root, replies } = thread;
  const entries: ThreadHistoryEntry[] = [
    {
      at: root.date,
      memberId: root.organization_member_id,
      action: "created the thread",
      detail: preview(root.text),
    },
  ];
  if (root.edited_at) {
    entries.push({
      at: root.edited_at,
      memberId: root.edited_by,
      action: "edited the thread",
    });
  }
  for (const reply of replies) {
    entries.push({
      at: reply.date,
      memberId: reply.organization_member_id,
      action: "replied",
      detail: preview(reply.text),
    });
    if (reply.edited_at) {
      entries.push({
        at: reply.edited_at,
        memberId: reply.edited_by,
        action: "edited a reply",
      });
    }
  }
  if (root.pinned_at) {
    entries.push({
      at: root.pinned_at,
      memberId: root.pinned_by,
      action: "pinned the thread",
    });
  }
  if (root.resolved_at) {
    entries.push({
      at: root.resolved_at,
      memberId: root.resolved_by,
      action: "marked it resolved",
    });
  }
  for (const view of views) {
    entries.push({
      at: view.first_viewed_at,
      memberId: view.member_id,
      action: "first saw it",
      detail:
        view.last_viewed_at !== view.first_viewed_at
          ? `Last seen ${formatAuditDate(view.last_viewed_at)}${
              view.view_count > 1 ? ` · ${view.view_count} visits` : ""
            }`
          : null,
    });
  }
  return entries.sort((a, b) => a.at.localeCompare(b.at));
};
